<template>
    <div class="confirmation-page">
        <v-card class="confirmation-card pa-8" elevation="10" rounded="xl">
            <!-- Loading State -->
            <div v-if="loading" class="d-flex flex-column justify-center align-center py-10">
                <v-progress-circular indeterminate color="primary" size="64" class="mb-4"></v-progress-circular>
                <h2 class="text-h6 text-primary font-weight-medium">Confirming your appointment...</h2>
                <p class="text-body-2 text-medium-emphasis mt-1">Please wait a moment while we update our records.</p>
            </div>


            <!-- Error State -->
            <div v-else-if="error" class="text-center py-6">
                <v-icon icon="mdi-alert-circle" color="error" size="64" class="mb-4"></v-icon>
                <h2 class="text-h5 text-error mb-2">Something went wrong</h2>
                <p class="text-body-1 text-medium-emphasis mb-6">
                    {{ errorMessage || 'We could not confirm your appointment.' }}
                </p>
                <v-btn color="primary" variant="flat" @click="confirmAppointment">
                    Try Again
                </v-btn>
            </div>

            <!-- Confirmation Success State -->
            <div v-else>
                <div class="text-center mb-6">
                    <v-icon color="success" size="80" class="mb-4 heart-beat">mdi-check-circle</v-icon>
                    <h1 class="text-h4 font-weight-bold text-success mb-2">
                        Confirmed!
                    </h1>
                    <p class="text-subtitle-1 text-medium-emphasis">
                        Your appointment has been successfully confirmed.
                    </p>
                </div>

                <v-divider v-if="appointment" class="mb-6"></v-divider>

                <div v-if="appointment" class="appointment-details px-4">
                    <div class="d-flex align-start mb-6 gap-3">
                        <v-icon color="primary" size="28" class="mt-1">mdi-calendar-clock</v-icon>
                        <div>
                            <div class="text-caption text-medium-emphasis text-uppercase font-weight-bold">Date & Time</div>
                            <div class="text-h6 font-weight-bold">{{ appointment.datePart }} at {{ appointment.timePart }}</div>
                        </div>
                    </div>

                    <div class="d-flex align-start gap-3 mb-4">
                        <v-icon color="primary" size="28" class="mt-1">mdi-map-marker</v-icon>
                        <div>
                            <div class="text-caption text-medium-emphasis text-uppercase font-weight-bold">Clinic Details</div>
                            <div class="text-subtitle-1 font-weight-medium line-height-tight">{{ appointment.clinicLocation }}</div>
                        </div>
                    </div>

                    <template v-if="appointment.upcomingAppointments && appointment.upcomingAppointments.length">
                        <v-divider class="my-5"></v-divider>
                        <div class="text-caption text-medium-emphasis text-uppercase font-weight-bold mb-3 d-flex align-center gap-2">
                            <v-icon color="primary" size="20">mdi-calendar-multiple</v-icon>
                            Your Upcoming Appointments (Next 10 Days)
                        </div>
                        <div
                            v-for="(appt, i) in appointment.upcomingAppointments"
                            :key="i"
                            class="upcoming-appt-row pa-3 mb-2 rounded-lg"
                        >
                            <div class="d-flex align-center gap-2 mb-1">
                                <v-icon color="primary" size="16">mdi-calendar-clock</v-icon>
                                <span class="text-body-2 font-weight-bold">{{ appt.datePart }} at {{ appt.timePart }}</span>
                            </div>
                            <div class="d-flex align-center gap-2">
                                <v-icon color="grey" size="16">mdi-map-marker</v-icon>
                                <span class="text-caption text-medium-emphasis">{{ appt.clinicLocation }}</span>
                            </div>
                        </div>

                        <div class="mt-4">
                            <v-btn
                                v-if="!allConfirmed"
                                color="primary"
                                variant="flat"
                                block
                                :loading="confirmingAll"
                                :disabled="confirmingAll"
                                @click="confirmAll"
                            >
                                <v-icon start>mdi-check-all</v-icon>
                                Confirm All Upcoming Appointments
                            </v-btn>
                            <v-alert
                                v-else
                                type="success"
                                variant="tonal"
                                class="mt-2"
                                density="compact"
                            >
                                All {{ confirmAllResult.confirmed }} appointment{{ confirmAllResult.confirmed !== 1 ? 's' : '' }} confirmed!
                            </v-alert>
                            <v-alert
                                v-if="confirmAllError"
                                type="error"
                                variant="tonal"
                                class="mt-2"
                                density="compact"
                            >
                                {{ confirmAllError }}
                            </v-alert>
                        </div>
                    </template>
                </div>
            </div>
        </v-card>
    </div>
</template>

<script>
import nonloginService from "../services/nonloginService";

export default {
    name: "AppointmentConfirmation",
    data() {
        return {
            loading: true,
            error: false,
            errorMessage: "",
            appointment: null,
            confirmingAll: false,
            allConfirmed: false,
            confirmAllResult: null,
            confirmAllError: null
        };
    },
    mounted() {
        this.confirmAppointment();
    },
    methods: {
        async confirmAppointment() {
            this.loading = true;
            this.error = false;
            this.appointment = null;
            const token = this.$route.params.token;

            try {
                const response = await nonloginService.confirmAppointment(token);

                if (response.status !== 200 || !response.data) {
                    throw new Error("Invalid response from server");
                }

                this.appointment = response.data.message;
            } catch (err) {
                console.error("Error confirming appointment:", err);
                this.error = true;
                this.errorMessage = err.response?.data?.message || "We could not confirm your appointment.";
            } finally {
                this.loading = false;
            }
        },

        async confirmAll() {
            this.confirmingAll = true;
            this.confirmAllError = null;
            const token = this.$route.params.token;

            try {
                const response = await nonloginService.confirmAllAppointments(token);
                this.confirmAllResult = response.data.message;
                this.allConfirmed = true;
            } catch (err) {
                console.error("Error confirming all appointments:", err);
                this.confirmAllError = err.response?.data?.message || "Could not confirm all appointments. Please contact the office.";
            } finally {
                this.confirmingAll = false;
            }
        }
    }
};
</script>


<style scoped>
.confirmation-page {
    min-height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    padding: 20px;
}

.confirmation-card {
    width: 100%;
    max-width: 480px;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
}

.heart-beat {
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.1);
    }

    100% {
        transform: scale(1);
    }
}

.gap-3 {
    gap: 12px;
}

.line-height-tight {
    line-height: 1.25 !important;
}

.upcoming-appt-row {
    background: #f5f7fa;
    border-left: 3px solid #5c6bc0;
}
</style>
